<template>
  <div class="col-1-1 text-dark">
    <IEwarning></IEwarning>
    <div class="flex column">
      <div class="hours_show__header width-100">
        <div class="flex space-between">
          <h1>{{ $t('vue2.substitute_hours.title') }}</h1>
          <h1 class="only-print right">{{ startDate | L }} - {{ endDate | L }}</h1>
          <div class="flex align-center">
            <p class="marg-10-right bold no-print">{{ $t('vue2.substitute_hours.choose_period') }}</p>
            <datePickerRange
              :allowSameDate="true"
              @startchanged="startDateChanged"
              @endchanged="endDateChanged"
              :startdate="startDate"
              :enddate="endDate"
              class="inline-block align-center flex no-print">
            </datePickerRange>
            <button class="marg-10-left no-print" @click="print">
              <span class="icon-printer3"></span>
            </button>
          </div>
        </div>
        <HoursShowShortcuts
          :start="start"
          :end="end">
        </HoursShowShortcuts>
        <dSelectUsers
          :showTags="true"
          :showCreateButton="false"
          @usersUpdated="syncUsers">
        </dSelectUsers>
      </div>
      <div class="filters marg-20-top marg-10-bottom flex">
        <div class="fl-icon icon-filter"></div>
        <button class="filter"
                :class="{'active': filterIsActive(null)}"
                @click="setEventsFilter(null)">{{ $t('vue2.substitute_hours.all') }}
        </button>
        <button class="filter"
                :class="{'active': filterIsActive('subject')}"
                @click="setEventsFilter('subject')">{{ $t('vue2.substitute_hours.teaching') }}
        </button>
        <button class="filter"
                :class="{'active': filterIsActive('substituteHours')}"
                @click="setEventsFilter('substituteHours')">{{ $t('vue2.substitute_hours.substitute_hours') }}
        </button>
        <button class="filter"
                :class="{'active': filterIsActive('absence')}"
                @click="setEventsFilter('absence')">{{ $t('vue2.substitute_hours.absence') }}
        </button>
      </div>
      <div class="hours_show__body width-100">
        <div v-if="isLoading" style="margin-top: 100px">
          <Spinner color="blue"></Spinner>
        </div>
        <ul class="user-list">
          <li class="user-list__user" v-for="user in usersAndEventsWithProps" :key="user.user_id">
            <div class="user-list__user__header">
              <h2>{{ user.name }}</h2>
              <div class="user-list__user__right">
                <p>{{ Object.keys(user.v_events_filtered).length }} {{ $t('vue2.substitute_hours.work_days') }}: <b>{{ user.v_all_days_duration }}</b></p>
                <p>{{ user.v_filter_events.length }}
                  <span
                    v-if="user.v_filter_events.length < 2">{{ $t('vue2.substitute_hours.calendar_event') }}</span>
                  <span v-else>{{ $t('vue2.substitute_hours.calendar_events') }}</span>:
                  <b>{{ getSumDuration(user.v_filter_events) }}</b>
                </p>
              </div>
            </div>
            <ul class="user-list__events">
              <li class="user-list__events_day" v-for="dayEvents in user.v_events_filtered"
                  :key="dayEvents[0].start_time">
                <div class="user-list__events_day__header">
                  <div>
                    <span
                      class="day-headline">{{
                        dayEvents[0].start_time | ddd | capitalize
                      }}, {{ dayEvents[0].start_time | LL }}</span>,
                    kl. {{ dayEvents[0].start_time | LT }} - {{ dayEvents[dayEvents.length - 1].end_time | LT }}, {{
                      getDayDuration(dayEvents)
                    }}
                  </div>
                  <div class="user-list__events_day__header__day-events">
                    <p>{{ dayEvents.length }}
                      <span v-if="dayEvents.length < 2">{{ $t('vue2.substitute_hours.calendar_event') }}</span>
                      <span v-else>{{ $t('vue2.substitute_hours.calendar_events') }}</span> -
                      {{ getSumDuration(dayEvents) }}
                    </p>
                  </div>
                </div>
                <ul class="user-list__event_day__lessons">
                  <li class="lesson"
                      v-for="event in dayEvents"
                      :key="event.event_id"
                      :style="{'borderColor': event.color}">
                    <div>
                      {{ event.start_time | LT }} - {{ event.end_time | LT }} | {{ event.title }} <span
                      v-if="eventHasAnyKlass(event.klasses)">,</span> {{ event.klasses | klassList }}
                    </div>
                    <div v-if="event.substitute_for">
                      <span class="icon-users3"></span>{{ $t('vue2.substitute_hours.substitute_for') }}:
                      {{ event.substitute_for }}
                    </div>
                    <div>
                      {{ getEventDuration(event) }}
                    </div>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import DatePickerRange from '../datepicker/DatePickerRange'
import dSelectUsers from '../DSelect/DSelectUsers.vue'
import moment from 'moment'
import filters from '../../filters/filters' // eslint-disable-line no-unused-vars
import docendo from '../../docendo' // eslint-disable-line no-unused-vars
import Spinner from '../common/Spinner'
import IEwarning from '../common/IEAndEdgeWarning'
import HoursShowShortcuts from './HoursShowShortcuts'

export default {
  name: 'HoursShow',
  components: {DatePickerRange, dSelectUsers, Spinner, IEwarning, HoursShowShortcuts},
  data () {
    return {
      startDate: moment().isoWeekday(1),
      endDate: moment().isoWeekday(7),
      selectedUsers: [],
      usersAndEvents: [],
      eventsFilter: null,
      isLoading: false
    }
  },
  computed: {
    schoolId () {
      return this.$store.state.activeSchool.id
    },
    start () {
      return moment(this.startDate).startOf('day')
    },
    end () {
      return moment(this.endDate).endOf('day')
    },
    selectedUsersIds () {
      return this.selectedUsers.map(user => {
        return user.id
      })
    },
    usersAndEventsSortedOnName () {
      return this.usersAndEvents.sort((a, b) => {
        var x = a.name.toLowerCase()
        var y = b.name.toLowerCase()
        if (x < y) {
          return -1
        }
        if (x > y) {
          return 1
        }
        return 0
      })
    },
    usersAndEventsWithProps () {
      return this.usersAndEventsSortedOnName.map(user => {
        user.v_events_filtered = this.filteredEvents(user.events)
        user.v_filter_events = this.filterEvents(user.events)
        user.v_day_durations = this.getUserDayDurations(user.v_events_filtered)
        user.v_all_days_duration = this.getAllUserDaysDurations(user.v_day_durations)
        return user
      })
    }
  },
  watch: {
    selectedUsersIds () {
      if (this.selectedUsersIds.length < 1) {
        this.usersAndEvents = []
      }
      this.getEvents()
    }
  },
  filters: {
    klassList (string) {
      return string.replace(/{|}/g, '')
    }
  },
  methods: {
    filterEvents (events) {
      let filteredEvents = events.filter(obj => {
        switch (this.eventsFilter) {
          case null:
            return obj.event_type !== ''
          case 'subject':
            return obj.event_type !== 'absence' && obj.event_type !== 'other_tasks' && obj.event_type !== 'other_stuff' && obj.substitute_for === null
          case 'substituteHours':
            return obj.substitute_for !== null
          case 'absence':
            return obj.event_type === 'absence'
        }
      })
      return filteredEvents
    },
    eventHasAnyKlass (event) {
      let str = event.replace(/{|}/g, '')
      return str.length > 0
    },
    eventsToArr (events) {
      events = this.eventsSortedInDays(events)
      return Object.keys(events).map(e => {
        return {date: e, events: events[e]}
      })
    },
    filteredEvents (events) {
      let filteredEvents = events.filter(obj => {
        switch (this.eventsFilter) {
          case null:
            return obj.event_type !== ''
          case 'subject':
            return obj.event_type !== 'absence' && obj.event_type !== 'other_tasks' && obj.event_type !== 'other_stuff' && obj.substitute_for === null
          case 'substituteHours':
            return obj.substitute_for !== null
          case 'absence':
            return obj.event_type === 'absence'
        }
      })
      return this.sortedEvents(filteredEvents)
    },
    sortedEvents (events) {
      let sortedEvent = events.sort((a, b) => {
        return moment(a.start_time).unix() - moment(b.start_time).unix()
      })
      return this.eventsSortedInDays(sortedEvent)
    },
    eventsSortedInDays (events) {
      return events.reduce((acc, curr, index) => {
        var val = moment(curr['start_time']).format('YYYY-MM-DD')
        acc[val] = acc[val] || []
        acc[val].push(curr)
        return acc
      }, {})
    },
    syncUsers (data) {
      this.selectedUsers = data
    },
    startDateChanged (date) {
      this.startDate = date
      this.getEvents()
    },
    endDateChanged (date) {
      this.endDate = date
      this.getEvents()
    },
    setEventsFilter (type) {
      this.eventsFilter = type
    },
    filterIsActive (type) {
      return this.eventsFilter === type
    },
    getEvents () {
      if (this.selectedUsersIds.length < 1) {
        return
      }
      this.isLoading = true
      docendo.schoolEvents(window.SCHOOL.id, this.start, this.end, this.selectedUsersIds, '', (data) => {
        this.usersAndEvents = data
        this.isLoading = false
      })
    },
    print () {
      window.print()
    },
    getSumDuration (eventsArr) {
      let filterAbsence = eventsArr
      if (this.eventsFilter !== 'absence') {
        filterAbsence = eventsArr.filter(lesson => lesson.event_type !== 'absence')
      }
      const durations = []
      filterAbsence.forEach(lesson => {
        durations.push(lesson.user_teaching_mins)
      })
      let data = durations.reduce((a, b) => a + b, 0)
      return this.humanizeMinutes(data)
    },
    getDayDuration (eventsArr) {
      let start = moment(eventsArr[0].start_time)
      let end = moment(eventsArr[eventsArr.length - 1].end_time)
      var duration = end.diff(start, 'minutes')
      return this.humanizeMinutes(duration)
    },
    getDayDurationInMinutes (eventsArr) {
      const start = moment(eventsArr[0].start_time)
      const end = moment(eventsArr[eventsArr.length - 1].end_time)
      return end.diff(start, 'minutes')
    },
    humanizeMinutes (data) {
      const minutes = data % 60
      const hours = (data - minutes) / 60
      const multiple = hours === 1 ? this.$i18n.t('vue2.substitute_hours.hour') : this.$i18n.t('vue2.substitute_hours.hours')
      return hours + ' ' + multiple + ' ' + minutes + ' ' + this.$t('vue2.substitute_hours.min')
    },
    getEventDuration (event) {
      if (this.activeFilter !== 'absence' && event.event_type === 'absence') {
        return ''
      }
      return event.user_teaching_mins + ' min.'
    },
    getUserDayDurations (eventsInDays) {
      let durations = {}
      for (const key in eventsInDays) {
        if (!durations[key]) {
          durations[key] = this.getDayDurationInMinutes(eventsInDays[key])
        }
      }
      return durations
    },
    getAllUserDaysDurations (dayDurations) {
      const durationsArr = Object.values(dayDurations)
      const allDaysDuration = durationsArr && durationsArr.length > 0 ? durationsArr.reduce((currentValue, sum) => currentValue + sum) : 0
      return this.humanizeMinutes(allDaysDuration)
    }
  },
  mounted () {
    if (window.Intercom) {
      window.Intercom('trackEvent', 'Visited Get User Events')
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.filters {
  align-items: center;
}

.filters > div {
  margin: 0 10px;
}

.filters > button {
  background: transparent;
  border-radius: 0;
  border-width: 1px 0 1px 1px;
  transition: all .25s ease-out;
  color: rgba(44, 62, 80, 0.7);
}

.filters > button:hover {
  background: white;
  color: #2c3e50;
}

.filters > button:active {
  border-color: #d8d8d8;
  transform: scale(0.95);
}

.filters > button.active {
  background: white;
  border-color: transparent;
  transform: scale(1.1);
  box-shadow: 0 2px 2px rgba(0, 0, 0, .1);
  color: #2c3e50;
}

.filters > button:nth-child(2) {
  border-radius: 4px 0 0 4px;
  border-right: 0;
}

.filters > button:last-child {
  border-radius: 0 4px 4px 0;
  border-width: 1px;
}

.fl-icon {
  opacity: 0.5;
}

.user-list__user {
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .07);
  background: #ffffff;
  margin-bottom: 20px;
  border: 1px solid #d8d8d8;
}

.user-list__user > h3 {
  padding: 10px;
}

.user-list__events {
  margin: 0;
}

.user-list__events_day {
  background-color: var(--bg-button);
  font-weight: 700;
}

.user-list__events_day__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  border: 1px solid var(--border-color);
  border-width: 1px 0 1px 0;
  padding: 8px;
}

.user-list__events_day > .user-list__event_day__lessons {
  background: #ffffff;
  margin: 0;
}

.user-list__event_day__lessons > .lesson {
  display: flex;
  justify-content: space-between;
  border-left: 2px solid transparent;
  margin: 1px 0;
  padding: 4px 8px;
  font-weight: normal;
  text-transform: initial;
}

.user-list__user__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px;
}

.user-list__events_day__header__day-events {
  font-weight: normal;
  text-align: right;
}

.shortcuts {
  margin-bottom: 1rem;
}

.user-list__user__right {
  text-align: right;
  padding: 0.5rem;
}

@media print {
  .user-list__user {
    page-break-inside: avoid;
  }
}
</style>



// WEBPACK FOOTER //
// HoursShow.vue?42638dcb